import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
// Import the original website styles first to ensure theme definitions are available
import './styles.css';
import './App.css';
// Additive responsive layer for the original themes. Imported last so it wins
// at equal specificity; the original stylesheets are left byte-identical.
import './responsive.css';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import GamingCSShowcase from './components/GamingCSShowcase';
import Footer from './components/Footer';
import About from './components/About';
import Projects from './components/Projects';
import Certifications from './components/Certifications';
import Hobbies from './components/Hobbies';
import Contact from './components/Contact';
import MyApps from './components/MyApps';
import useCustomCursor from './hooks/useCustomCursor';
import useRobotAssistant from './hooks/useRobotAssistant';
import useThemeEffects from './hooks/useThemeEffects';
import { themes, resolveThemeId } from './themeConfig';
import ModernistSite from './modernist/ModernistSite';
import ThemePicker from './components/ThemePicker';

// Create a component to handle scroll to top on route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [currentTheme, setCurrentTheme] = useState('original');
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    // Load saved theme from localStorage, dropping back to the default if the
    // stored theme has since been retired.
    const savedTheme = resolveThemeId(localStorage.getItem('selectedTheme'));
    setCurrentTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
    localStorage.setItem('selectedTheme', savedTheme);

    return () => clearTimeout(timer);
  }, []);

  // The Modernist pack is a different presentation, not a restyle: it supplies
  // its own nav, pages and footer, so the original site's chrome must stand
  // down rather than be hidden with CSS.
  const isModernist = currentTheme.startsWith('modernist-');

  // Initialize custom cursor, robot assistant, and theme effects
  useCustomCursor();
  useRobotAssistant(!isLoading && !isModernist);
  useThemeEffects(currentTheme, !isLoading && !isModernist);

  // Change theme function - optimized with useCallback
  const changeTheme = useCallback((themeId) => {
    setCurrentTheme(themeId);
    document.documentElement.setAttribute('data-theme', themeId);
    localStorage.setItem('selectedTheme', themeId);
    
    setIsThemeModalOpen(false);

    // Show theme confirmation
    showThemeConfirmation(themeId);
  }, []);

  // Show theme confirmation message
  const showThemeConfirmation = useCallback((themeId) => {
    // Find the theme name
    const theme = themes.find(t => t.id === themeId);
    if (!theme) return;
    
    // Create confirmation element
    const confirmation = document.createElement('div');
    confirmation.className = 'theme-confirmation';
    confirmation.textContent = `Theme changed to: ${theme.name}`;
    confirmation.style.cssText = `
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: var(--gradient-primary);
        color: white;
        padding: 12px 24px;
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-lg);
        z-index: 10002;
        font-weight: 600;
        animation: slideUp 0.3s ease, fadeOut 0.5s ease 2.5s forwards;
    `;
    
    document.body.appendChild(confirmation);
    
    // Add animation styles if not already present
    if (!document.querySelector('#theme-confirmation-styles')) {
      const style = document.createElement('style');
      style.id = 'theme-confirmation-styles';
      style.textContent = `
          @keyframes slideUp {
              from { 
                  transform: translate(-50%, 20px);
                  opacity: 0;
              }
              to { 
                  transform: translate(-50%, 0);
                  opacity: 1;
              }
          }
          
          @keyframes fadeOut {
              from { opacity: 1; }
              to { opacity: 0; }
          }
      `;
      document.head.appendChild(style);
    }
    
    // Remove element after animation
    setTimeout(() => {
      if (confirmation.parentNode) {
        confirmation.parentNode.removeChild(confirmation);
      }
    }, 3000);
  }, []);

  // Open theme modal - optimized with useCallback
  const openThemeModal = useCallback(() => {
    setIsThemeModalOpen(true);
  }, []);

  // Close theme modal - optimized with useCallback
  const closeThemeModal = useCallback(() => {
    setIsThemeModalOpen(false);
  }, []);

  if (isLoading) {
    return (
      <div id="cosmic-loader" className="cosmic-loader">
        <div className="warp-tunnel">
          <div className="stars"></div>
          <div className="stars"></div>
          <div className="stars"></div>
        </div>
        
        {/* Gaming Icons Animation */}
        <div className="gaming-icons">
          {/* Rockets */}
          <div className="gaming-icon rocket" style={{'--delay': '0s', '--orbit': '150px'}}>
            <i className="fas fa-rocket"></i>
          </div>
          <div className="gaming-icon rocket" style={{'--delay': '1s', '--orbit': '180px'}}>
            <i className="fas fa-rocket"></i>
          </div>
          
          {/* Gaming Console */}
          <div className="gaming-icon console" style={{'--delay': '0.5s', '--orbit': '200px'}}>
            <i className="fas fa-gamepad"></i>
          </div>
          <div className="gaming-icon console" style={{'--delay': '1.5s', '--orbit': '120px'}}>
            <i className="fas fa-gamepad"></i>
          </div>
          
          {/* Laptops */}
          <div className="gaming-icon laptop" style={{'--delay': '0.3s', '--orbit': '170px'}}>
            <i className="fas fa-laptop"></i>
          </div>
          <div className="gaming-icon laptop" style={{'--delay': '1.3s', '--orbit': '220px'}}>
            <i className="fas fa-laptop"></i>
          </div>
        </div>
        
        <div className="loader-content">
          <div className="terminal-text">
            <span className="terminal-prompt">$</span>
            <span className="typing-text" id="typing-text">Booting Portfolio.exe...</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        {/* Scroll to top on route changes */}
        <ScrollToTop />
        
        {/* Robot Assistant */}
        {!isModernist && (
        <div id="robot-assistant" className="robot-assistant" data-tooltip="Click me to get helpful tips!">
          <div className="robot-body">
            <div className="robot-head">
              <div className="robot-eyes">
                <div className="eye left-eye"></div>
                <div className="eye right-eye"></div>
              </div>
            </div>
          </div>
          <div className="tooltip" id="tooltip">
            <span className="tooltip-text">Welcome to my cosmic portfolio!</span>
          </div>
        </div>
        )}

        {/* Theme picker */}
        {isThemeModalOpen && (
          <ThemePicker
            themes={themes}
            currentTheme={currentTheme}
            onSelect={changeTheme}
            onClose={closeThemeModal}
          />
        )}

        {isModernist ? (
          /* A whole different presentation: its own nav, pages and footer, with
             the same routes so deep links and the back button still work. The
             theme modal above stays mounted, so the other themes remain one
             click away. */
          <ModernistSite
            theme={currentTheme}
            onTheme={changeTheme}
            openThemeModal={openThemeModal}
          />
        ) : (
        <Routes>
          <Route path="/" element={
            <>
              <Navigation openThemeModal={openThemeModal} />
              {/* Pass openThemeModal function to Hero component */}
              <Hero currentTheme={currentTheme} openThemeModal={openThemeModal} />
              <GamingCSShowcase />
              <Footer />
            </>
          } />
          <Route path="/about" element={
            <>
              <Navigation openThemeModal={openThemeModal} />
              <About currentTheme={currentTheme} />
              <Footer />
            </>
          } />
          <Route path="/projects" element={
            <>
              <Navigation openThemeModal={openThemeModal} />
              <Projects />
              <Footer />
            </>
          } />
          <Route path="/certifications" element={
            <>
              <Navigation openThemeModal={openThemeModal} />
              <Certifications />
              <Footer />
            </>
          } />
          <Route path="/hobbies" element={
            <>
              <Navigation openThemeModal={openThemeModal} />
              <Hobbies />
              <Footer />
            </>
          } />
          <Route path="/contact" element={
            <>
              <Navigation openThemeModal={openThemeModal} />
              <Contact />
              <Footer />
            </>
          } />
          <Route path="/myapps" element={
            <>
              <Navigation openThemeModal={openThemeModal} />
              <MyApps />
              <Footer />
            </>
          } />
        </Routes>
        )}
      </div>
    </Router>
  );
}

export default App;
